import { useEffect, useState } from "react";

import { Badge, TBadgeVariants } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { TableCell } from "@/components/ui/table";
import { TGetWeighScale } from "@/interfaces/wastetype.interface";
import { cn, formatToCapitalizedWords } from "@/lib/utils";
import {
  MONTH_DAY_YEAR_FORMAT,
  dateFormat,
} from "@/utils/helper-functions/date-time.helper";

interface IPreviewTicketProps {
  isOpen: boolean;
  toggleModal: () => void;
}

interface IFormattedDetails {
  projectDetails: {
    name: string;
    projectNumber: string;
    status: string;
    startDate: string;
    isGreenStarProject: boolean;
    address: string;
  };
  customerDetails: {
    name: string;
    phoneNumber: string;
    address: string;
    status: string;
    abn: string;
  };
  weighScaleTicketItems: {
    loadType: {
      name: string;
      tareWeight: number;
    };
    wasteType: {
      name: string;
      recoveryPathway: string;
      recyclingPercentage: number;
    };
    loadTypeQuantity: number;
    wasteTypeWeight: number;
    breakDownData: {
      mixedWaste: {
        wasteName: string;
        percentage: number;
      }[];
      otherWaste: {
        wasteName: string;
      }[];
    };
  }[];
}

export const DisplayCard = ({
  dataHeader,
  data,
  className,
  headerClassName,
  showBadge,
}: {
  dataHeader: string;
  data: string;
  className?: string;
  headerClassName?: string;
  showBadge?: {
    header: string[];
    badgeVariant: (status: string) => TBadgeVariants;
  };
}): JSX.Element => {
  return (
    <div className='flex flex-col '>
      <TableCell
        className={cn(
          "border-b w-full pl-0 grid grid-cols-3 gap-2 ",
          className,
        )}
      >
        <span className={cn(" text-gray-400 uppercase ", headerClassName)}>
          {formatToCapitalizedWords(dataHeader)}
        </span>
        {showBadge && showBadge.header.includes(dataHeader) && data ? (
          <span>
            <Badge
              className='col-span-2'
              variant={showBadge.badgeVariant(data)}
            >
              {data}
            </Badge>
          </span>
        ) : (
          <span className='col-span-2 lowercase truncate text-balance'>
            {data ?? "-"}
          </span>
        )}
      </TableCell>
    </div>
  );
};

const getStatusColor = (status: string): TBadgeVariants => {
  switch (status) {
    case "COMPLETED":
    case "ACTIVE":
      return "success";
    case "ARCHIVE":
      return "lightViolet";
    case "DEACTIVATED":
      return "destructive";
    default:
      return "yellow";
  }
};

const useFormattedDetails = (
  ticket: TGetWeighScale | null,
): IFormattedDetails | undefined => {
  const [formattedDetails, setFormattedDetails] = useState<IFormattedDetails>();

  useEffect(() => {
    if (!ticket) {
      return;
    }

    const projectDetails = {
      name: ticket?.project?.name || "",
      projectNumber: ticket?.project?.customerProjectReference || "",
      status: ticket?.project?.status || "",
      startDate: ticket?.project?.startDate
        ? dateFormat(ticket?.project?.startDate, MONTH_DAY_YEAR_FORMAT)
        : "Not Available",
      isGreenStarProject: ticket?.project?.isGreenStarProject || false,
      address: ticket?.project?.address?.label || "",
    };

    const customerDetails = {
      name: ticket?.customer?.name || "",
      phoneNumber: ticket?.customer?.phoneNumber || "",
      address: ticket?.customer?.address?.label || "",
      status: ticket?.customer?.status || "",
      abn: ticket?.customer?.abn || "",
    };

    const weighScaleTicketItems =
      ticket?.weighScaleTicketItems?.map((item) => ({
        loadType: {
          name: item.loadType.name,
          tareWeight: item.loadType.tareWeight,
        },
        wasteType: {
          name: item.wasteType.name,
          recoveryPathway: item.wasteType.recoveryPathway,
          recyclingPercentage: item.wasteType.recyclingPercentage,
        },
        loadTypeQuantity: item.loadTypeQuantity,
        wasteTypeWeight: item.wasteTypeWeight,
        breakDownData: {
          mixedWaste: item.breakDownData.mixedWaste || [],
          otherWaste: item.breakDownData.otherWaste || [],
        },
      })) || [];

    setFormattedDetails({
      projectDetails,
      customerDetails,
      weighScaleTicketItems,
    });
  }, [ticket]);

  return formattedDetails;
};

const renderDisplayCards = (details: IFormattedDetails): JSX.Element => {
  const renderCards = (
    // eslint-disable-next-line
    data: Record<string, any>,
    title: string,
  ): JSX.Element => (
    <>
      <h2 className='mt-4 text-xl font-medium'>{title}</h2>
      {Object.entries(data).map(([key, value]) => {
        //remove isGreenStarProject for now as we dont have any green star project.
        if (key === "isGreenStarProject") {
          return null;
        }

        return (
          <DisplayCard
            key={key}
            dataHeader={key}
            headerClassName='text-gray-500'
            // eslint-disable-next-line
            data={typeof value === "boolean" ? (value ? "Yes" : "No") : value}
            showBadge={{
              header: ["status"],
              badgeVariant: getStatusColor,
            }}
          />
        );
      })}
    </>
  );

  return (
    <>
      {renderCards(details.projectDetails, "Project Details")}
      {renderCards(details.customerDetails, "Customer Details")}
      {details.weighScaleTicketItems.map((item, index) => (
        <div key={index}>
          <h2 className='mt-4 text-xl font-medium'>Weigh Scale Ticket Items</h2>
          {renderCards(item.loadType, "Load Type")}
          {renderCards(item.wasteType, "Waste Type")}
          <DisplayCard
            dataHeader='Quantity'
            data={item.loadTypeQuantity.toString()}
            headerClassName='text-gray-500'
          />
          <DisplayCard
            dataHeader='Weight'
            headerClassName='text-gray-500'
            data={item.wasteTypeWeight.toString()}
          />
          {item.breakDownData.mixedWaste.length > 0 && (
            <>
              <h3 className='mt-2 text-lg font-semibold'>Mixed Waste</h3>
              {item.breakDownData.mixedWaste.map((mixedWaste, mwIndex) => (
                <DisplayCard
                  key={mwIndex}
                  dataHeader={mixedWaste.wasteName}
                  data={`${mixedWaste.percentage}%`}
                  headerClassName='text-gray-500'
                />
              ))}
            </>
          )}
          {item.breakDownData.otherWaste.length > 0 && (
            <>
              <h3 className='mt-2 text-lg font-semibold'>Other Waste</h3>
              {item.breakDownData.otherWaste.map((otherWaste, owIndex) => (
                <DisplayCard
                  key={owIndex}
                  dataHeader='Waste Name'
                  data={otherWaste.wasteName}
                  headerClassName='text-gray-500'
                />
              ))}
            </>
          )}
        </div>
      ))}
    </>
  );
};

const PreviewTicket = ({
  isOpen,
  toggleModal,
}: IPreviewTicketProps): JSX.Element => {
  const ticket: TGetWeighScale | null = JSON.parse(
    localStorage.getItem("ticketData") || "null",
  );
  const formattedDetails = useFormattedDetails(ticket);

  const handleExit = (): void => {
    localStorage.removeItem("ticketData");
    toggleModal();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleExit}>
      <SheetContent
        title='Preview Ticket'
        className='pt-12 sm:max-w-[400px] lg:max-w-[650px] space-y-4'
      >
        <h1 className='mb-3 text-3xl'>Ticket Details:</h1>
        <ScrollArea className='max-h-[90vh] overflow-y-scroll scrollbar-none'>
          <div className='space-y-4 mb-16'>
            {formattedDetails ? renderDisplayCards(formattedDetails) : null}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default PreviewTicket;
